/**
 * QuizImage Component
 * Renders quiz images or videos (not Desmos graphs - use QuizMedia for that)
 * Ported from reference_legacy/quebon-lms-web/components/lesson/quiz-types/shared/quiz-image.tsx
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import type { QuizMediaObject } from '../../../types/legacyQuiz'
import { QuizVideoPlayer } from './quiz-video-player'

interface QuizImageProps {
  /** Main image/video object */
  image?: QuizMediaObject | null
  /** Mobile-specific image (used on screens <=768px, skipped if image is video) */
  imageMobile?: QuizMediaObject | null
  /** Additional CSS classes */
  className?: string
  /** Alt text for image */
  alt?: string
}

/**
 * Determines if a media object is a video based on mimeType
 */
function isVideoMedia(media: QuizMediaObject | null | undefined): boolean {
  return !!(media?.mimeType && media.mimeType.startsWith('video/'))
}

/**
 * Gets the appropriate URL for a media object
 * For videos: prioritizes hls over signedUrl/url
 * For images: uses signedUrl, fallback to url
 */
function getMediaUrl(media: QuizMediaObject | null | undefined): string | null {
  if (!media) return null
  if (isVideoMedia(media)) {
    return media.hls ?? media.signedUrl ?? media.url ?? null
  }
  return media.signedUrl ?? media.url ?? null
}

/**
 * QuizImage component for displaying quiz images or videos
 *
 * Rendering rules:
 * - If image is a video: renders video player, imageMobile is ignored
 * - If image is an image:
 *   - Mobile (<=768px): shows imageMobile if available, otherwise shows image
 *   - Desktop (>=769px): shows image only
 */
export function QuizImage({
  image,
  imageMobile,
  className,
  alt,
}: QuizImageProps) {
  const { t } = useTranslation('quiz')
  const imageAlt = alt ?? t('container.questionImage')
  // Calculate scale if provided - must be called before any early returns
  const scaleStyle = useMemo(() => {
    if (image?.scaleX || image?.scaleY) {
      const scaleX = image.scaleX ?? 1
      const scaleY = image.scaleY ?? 1
      // Use transform scale if both are the same, otherwise use width/height
      if (scaleX === scaleY && scaleX !== 1) {
        return { zoom: scaleX }
      }
    }
    return undefined
  }, [image?.scaleX, image?.scaleY])

  // Early return if no image
  if (!image) return null

  const isVideo = isVideoMedia(image)
  const videoUrl = getMediaUrl(image)
  const imageUrl = getMediaUrl(image)
  const mobileImageUrl = getMediaUrl(imageMobile)
  const hasHls = !!(image?.hls)

  // For videos: render video player (ignore imageMobile)
  if (isVideo && videoUrl) {
    return (
      <div className={cn('flex justify-center my-4', className)}>
        <QuizVideoPlayer
          src={videoUrl}
          mimeType={image.mimeType}
          isHls={hasHls}
          className="overflow-hidden"
        />
      </div>
    )
  }

  // For images: handle mobile/desktop responsive display
  if (!imageUrl) return null

  // If there's a mobile image, show it on mobile and hide on desktop
  if (mobileImageUrl && mobileImageUrl !== imageUrl) {
    return (
      <div className={cn('flex justify-center my-4', className)}>
        {/* Mobile image - shown on screens <=768px, hidden on desktop */}
        <img
          src={mobileImageUrl}
          alt={imageAlt}
          className="block max-w-full h-auto md:hidden"
          style={scaleStyle}
          referrerPolicy="no-referrer"
        />
        {/* Desktop image - hidden on mobile, shown on screens >=768px */}
        <img
          src={imageUrl}
          alt={imageAlt}
          className="hidden max-w-full h-auto md:block"
          style={scaleStyle}
          referrerPolicy="no-referrer"
        />
      </div>
    )
  }

  // No mobile image, show the same image on all screens
  return (
    <div className={cn('flex justify-center my-4', className)}>
      <img
        src={imageUrl}
        alt={imageAlt}
        className="max-w-full h-auto"
        style={scaleStyle}
        referrerPolicy="no-referrer"
      />
    </div>
  )
}
